import React, { Fragment, useEffect, useState } from "react"
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'react-bootstrap'
import Select from 'react-select'
import { format } from 'date-fns'

import FilterSearch from 'pages/parts/FilterSearch'
import Pagination, { ItemPerPage } from 'pages/parts/Pagination'

import { GET, PGET } from 'helpers/api'
import { formatMoney, findFromArrayById } from 'helpers/helpers'

import ShiftStatus, { SHIFT_STATUSES } from './ShiftStatus'
import ShiftCloseFlow from './shiftClose/ShiftCloseFlow'

const URL = '/services/web/api/cash-shifts'

const EMPTY_FILTER = { posId: null, status: null }

// quick status filter chips; null = all
const STATUS_CHIPS = [null, 'open', 'pending', 'closed']

// backend shiftStatus -> SHIFT_STATUSES key ("OPEN" -> "open")
function getStatus(item) {
	return item.shiftStatus?.toLowerCase()
}

function formatAmount(amount) {
	return formatMoney(amount, Number.isInteger(amount) ? 0 : 2)
}

// backend sends "Сум" / "Доллар США" / "USD"...
function currencyLabel(currency) {
	const name = (currency || '').toLowerCase()
	if (name.includes('usd') || name.includes('доллар') || name.includes('dollar')) return '$'
	if (name.includes('uzs') || name.includes('сум') || name.includes("so'm") || name.includes('sum')) return 'UZS'
	return currency || ''
}

// card + cash summed per currency, UZS first
function sumByCurrency(rows) {
	const totals = {}
	rows.forEach(row => {
		const currency = currencyLabel(row.currencyName)
		totals[currency] = (totals[currency] || 0) + (Number(row.balance) || 0)
	})
	return Object.entries(totals).sort(([a], [b]) => (b === 'UZS') - (a === 'UZS'))
}

// time since the date: "31 ч 56 мин"
function formatDuration(date, t) {
	const minutes = Math.max(0, Math.floor((Date.now() - new Date(date).getTime()) / 60000))
	const hours = Math.floor(minutes / 60)
	const rest = `${minutes % 60} ${t('MINUTES_SHORT')}`
	return hours > 0 ? `${hours} ${t('HOURS_SHORT')} ${rest}` : rest
}

// hours since the date -> warning / danger
const AGE_LIMITS = {
	open: { warning: 16, danger: 24 },
	pending: { warning: 2, danger: 12 },
}

function ageLevel(status, date) {
	const limits = AGE_LIMITS[status]
	if (!limits || !date) return ''
	const hours = (Date.now() - new Date(date).getTime()) / 3600000
	if (hours >= limits.danger) return 'danger'
	if (hours >= limits.warning) return 'warning'
	return ''
}

function formatId(id) {
	return String(id).padStart(5, '0')
}

function formatDateTime(value, pattern = 'dd.MM.yyyy HH:mm') {
	return value ? format(new Date(value), pattern) : ''
}

function CashRegistersTab({ onTotalChange }) {
	const { t } = useTranslation()

	const [data, setData] = useState([])
	const [total, setTotal] = useState(0)
	const [poses, setPoses] = useState([])
	const [search, setSearch] = useState('')
	const [filter, setFilter] = useState(EMPTY_FILTER)
	const [filterDraft, setFilterDraft] = useState(EMPTY_FILTER)
	const [filterOpen, setFilterOpen] = useState(false)
	const [page, setPage] = useState(0)
	const [size, setSize] = useState(20)
	const [closingShift, setClosingShift] = useState(null)
	const [counts, setCounts] = useState({})

	const posOptions = poses.map(pos => ({ value: pos.id, label: pos.name }))
	const statusOptions = Object.entries(SHIFT_STATUSES).map(([value, i18n]) => ({ value, label: t(i18n) }))

	// backend has no search param: search filters the loaded page only
	const query = search.trim().toLowerCase()
	const pageData = data.filter(item =>
		!query || [formatId(item.cashboxId), item.cashName, item.shiftNumber, item.posName, item.cashierName].some(value => value?.toLowerCase().includes(query))
	)
	const pageCount = Math.ceil(total / size)

	async function getData() {
		const response = await PGET(URL, {
			posId: filter.posId,
			status: filter.status?.toUpperCase(),
			page,
			size,
		}, { loader: true })
		if (response && response.data) {
			setData(response.data)
			setTotal(Number(response.headers['x-total-count']) || 0)
		}
		getCounts()
	}

	// per-status counts for the chips and the tab badge (pending)
	async function getCounts() {
		const statuses = STATUS_CHIPS.filter(Boolean)
		const responses = await Promise.all(statuses.map(status =>
			PGET(URL, { posId: filter.posId, status: status.toUpperCase(), page: 0, size: 1 })
		))
		const newCounts = {}
		statuses.forEach((status, index) => {
			newCounts[status] = Number(responses[index]?.headers?.['x-total-count']) || 0
		})
		newCounts.all = statuses.reduce((sum, status) => sum + newCounts[status], 0)
		setCounts(newCounts)
		onTotalChange?.(newCounts.pending)
	}

	async function getPoses() {
		const response = await GET('/services/web/api/pos-helper')
		if (response) {
			setPoses(response)
		}
	}

	// cash transfer is already sent by ShiftCloseFlow, reload to get the real shift status
	function closeShift() {
		setClosingShift(null)
		getData()
	}

	function toggleFilter(isOpen) {
		if (isOpen) setFilterDraft(filter)
		setFilterOpen(isOpen)
	}

	function applyFilter(newFilter) {
		setFilter(newFilter)
		setFilterDraft(newFilter)
		setPage(0)
		setFilterOpen(false)
	}

	function resetFilter() {
		setSearch('')
		applyFilter(EMPTY_FILTER)
	}

	function selectStatus(status) {
		if (status !== filter.status) applyFilter({ ...filter, status })
	}

	function renderAction(item) {
		const status = getStatus(item)
		if (status === 'pending') {
			return (
				<button type="button" className="btn tp-action-btn tp-action-confirm" onClick={() => setClosingShift(item)}>
					<i className="uil uil-check"></i>
					{t('CHECK_SHIFT')}
				</button>
			)
		}
		if (status === 'open') {
			return (
				<button type="button" className="btn tp-action-btn tp-action-close" onClick={() => setClosingShift(item)}>
					<i className="uil uil-lock"></i>
					{t('CLOSE_CASHBOX')}
				</button>
			)
		}
		return null
	}

	useEffect(() => {
		getPoses()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	useEffect(() => {
		getData()
	}, [filter, page, size]) // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<>
			<div className="d-flex mb-3">
				<div>
					{/* search is local (backend has no search param), FilterSearch only gives the standard input */}
					<FilterSearch
						filterData={{ search }}
						setFilterData={(update) => setSearch(update({}).search)}
						disableGroupSearch>
					</FilterSearch>
				</div>
				<div>
					<Dropdown show={filterOpen} onToggle={toggleFilter} autoClose="outside">
						<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100 me-2">
							{t('FILTER')}
						</Dropdown.Toggle>
						<Dropdown.Menu className="dropdown-filter">
							<div className="row">
								<div className="form-group col-md-6">
									<label>{t('STORE')}</label>
									<Select
										options={posOptions}
										value={posOptions.find(option => option.value === filterDraft.posId) || null}
										onChange={(option) => setFilterDraft({ ...filterDraft, posId: option?.value || null })}
										isClearable
										placeholder={t('ALL')}
										noOptionsMessage={() => t('LIST_IS_EMPTY')}
									/>
								</div>
								<div className="form-group col-md-6">
									<label>{t('SHIFT_STATUS')}</label>
									<Select
										options={statusOptions}
										value={statusOptions.find(option => option.value === filterDraft.status) || null}
										onChange={(option) => setFilterDraft({ ...filterDraft, status: option?.value || null })}
										isClearable
										isSearchable={false}
										placeholder={t('ALL')}
									/>
								</div>
							</div>
							<div className="row mt-3">
								<div className="col-md-12">
									<div className="d-flex justify-content-end">
										<button type="button" className="btn btn-secondary me-2" onClick={() => setFilterOpen(false)}>{t('CANCEL')}</button>
										<button type="button" className="btn btn-primary" onClick={() => applyFilter(filterDraft)}>{t('APPLY')}</button>
									</div>
								</div>
							</div>
						</Dropdown.Menu>
					</Dropdown>
				</div>
			</div>

			{(filter.posId || filter.status || search) &&
				<div className="filter-block mb-3">
					<div className="filter">
						<strong>{t('FILTER')}</strong>
						<br />
						{filter.posId &&
							<span className="me-2">
								<b>{t('STORE')}: </b> {findFromArrayById(poses, filter.posId)}
							</span>
						}
						{filter.status &&
							<span className="me-2">
								<b>{t('SHIFT_STATUS')}: </b> {t(SHIFT_STATUSES[filter.status])}
							</span>
						}
						{search &&
							<span className="me-2">
								<b>{t('SEARCH')}: </b>
								<span>{search}</span>
							</span>
						}
					</div>
					<div className="vertical-center">
						<button type="button" className="btn btn-outline-dark text-uppercase" onClick={resetFilter}>{t('RESET')}</button>
					</div>
				</div>
			}

			<div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
				<div className="tp-chips">
					{STATUS_CHIPS.map(status => (
						<button type="button" key={status || 'all'}
							className={'tp-chip' + (filter.status === status ? ' active' : '')}
							onClick={() => selectStatus(status)}>
							{status ? t(SHIFT_STATUSES[status]) : t('ALL')}
							<span className="tp-chip-count">{counts[status || 'all'] ?? 0}</span>
						</button>
					))}
				</div>
				<ItemPerPage itemsPerPage={size} setItemsPerPage={(value) => { setSize(value); setPage(0) }} />
			</div>

			<div className="table-responsive">
				<table className="table table-striped mb-0">
					<thead>
						<tr>
							<th style={{ width: '3%' }}>№</th>
							<th style={{ width: '10%' }}>{t('CASHBOX')}</th>
							<th style={{ width: '22%' }}>{t('TRADING_POINT')}</th>
							<th style={{ width: '17%' }}>{t('CASHIER')}</th>
							<th style={{ width: '13%' }} className="text-end">{t('IN_CASHBOX')}</th>
							<th style={{ width: '22%' }}>{t('SHIFT')}</th>
							<th style={{ width: '13%' }}>{t('ACTION')}</th>
						</tr>
					</thead>
					<tbody>
						{pageData.length > 0 ? pageData.map((item, index) => {
							const balanceTotals = sumByCurrency(item.cashShiftBalanceDTOList || [])
							const status = getStatus(item)
							const isClosed = status === 'closed'
							const statusDate = item.statusDate
							const age = isClosed ? '' : ageLevel(status, statusDate)

							return (
								<tr key={item.shiftId}>
									<td>{page * size + index + 1}</td>
									<td>
										<div className="tp-name" title={item.shiftNumber ? `${t('SHIFT_NUMBER')}: ${item.shiftNumber}` : undefined}>{item.cashName}</div>
									</td>
									<td>{item.posName}</td>
									<td>{item.cashierName || '-'}</td>
									<td className="text-end">
										{balanceTotals.length > 0 ?
											<div className="tp-balance">
												{balanceTotals.map(([currency, amount]) => (
													<Fragment key={currency}>
														<span className="tp-balance-amount">{formatAmount(amount)}</span>
														<span className="tp-balance-currency">{currency}</span>
													</Fragment>
												))}
											</div>
											:
											'-'
										}
									</td>
									<td>
										<ShiftStatus status={status} />
										{statusDate &&
											<div className={`tp-status-meta ${age ? 'tp-age-' + age : ''}`}>
												<i className={`uil ${age === 'danger' ? 'uil-exclamation-circle' : 'uil-clock'}`}></i>
												<span className="tp-date-hover">
													{formatDateTime(statusDate)}
													<span className="tp-tooltip">
														<span className="tp-tooltip-label">{isClosed ? t('CLOSED_AGO') : t('OPENED_AGO')}</span>
														<span className="tp-tooltip-value">
															<span className="tp-tooltip-dot"></span>
															{formatDuration(statusDate, t)}
														</span>
													</span>
												</span>
											</div>
										}
									</td>
									<td>{renderAction(item)}</td>
								</tr>
							)
						}) : <tr>
								<td colSpan="7" className="text-center">{t('NO_DATA')}</td>
							</tr>
						}
					</tbody>
				</table>
			</div>

			<Pagination pageCount={pageCount} paginate={(data) => setPage(data.selected)} reduxPage={page}></Pagination>

			<ShiftCloseFlow
				shift={closingShift}
				onHide={() => setClosingShift(null)}
				onFinish={closeShift}
			/>
		</>
	)
}

export default CashRegistersTab
